const { zip, keyBy, sortBy, groupBy, sumBy, get, sum, flatMap, max, } = require('lodash');

const { floatFormat, integerFormat } = require('../util');
const { accountItemCategories, accountItemCategoryNames } = require('../config');

const { keys, entries, } = Object;
const { abs } = Math;
const accountItemCategoriesByName = keyBy(accountItemCategories, 'name');
const accountItemCategoriesGroupedByType = groupBy(accountItemCategories, 'type');

// 純資産のカテゴリで末端のもの（子がないもの）を取得（当期純損益金額は除外）
const netAssetEndCategories = accountItemCategoriesByName['純資産'].descendants().filter(_ => _.children().length === 0 && _.name !== '当期純損益金額');

// ヘッダー階層を構築する関数
const buildHeaderTree = (allConsolidationAccountItems) => {
  const netAssetItems = allConsolidationAccountItems.filter(_ =>
    _.itemType === 'consolidationAccountItem' &&
    netAssetEndCategories.some(cat => cat.name === _.accountItemCategory?.name)
  );
  const itemsGroupedByCategory = groupBy(netAssetItems, _ => _.accountItemCategory?.name);

  // 再帰的にヘッダーツリーを構築
  const buildNode = (category, depth = 0) => {
    // 当期純損益金額カテゴリは除外
    const childCategories = category.children().filter(_ => _.name !== '当期純損益金額');
    const isEndCategory = childCategories.length === 0;

    if (isEndCategory) {
      // 末端カテゴリ: 科目 + 計
      const items = itemsGroupedByCategory[category.name] || [];
      const itemHeaders = items.map(item => ({
        type: 'item',
        item,
        category,
        label: item.consolidationAccountItem?.name || item.item?.name,
        depth: depth + 1,
        colspan: 1,
        rowspan: null, // 後で計算
      }));
      const totalHeader = {
        type: 'categoryTotal',
        category,
        label: category.name + ' 計',
        depth: depth + 1,
        colspan: 1,
        rowspan: null,
      };
      return {
        type: 'category',
        category,
        label: category.name,
        depth,
        colspan: items.length + 1, // 科目数 + 計
        rowspan: 1,
        children: [...itemHeaders, totalHeader],
      };
    } else {
      // 中間カテゴリ: 子カテゴリ + 計
      const childNodes = childCategories.map(child => buildNode(child, depth + 1));
      const totalHeader = {
        type: 'categoryTotal',
        category,
        label: category.name + ' 計',
        depth: depth + 1,
        colspan: 1,
        rowspan: null,
      };
      const childColspan = sumBy(childNodes, 'colspan') + 1; // 子の合計 + 計
      return {
        type: 'category',
        category,
        label: category.name,
        depth,
        colspan: childColspan,
        rowspan: 1,
        children: [...childNodes, totalHeader],
      };
    }
  };

  // 純資産から構築
  const netAssetCategory = accountItemCategoriesByName['純資産'];
  const tree = buildNode(netAssetCategory, 0);

  // ツリーをフラットなヘッダー配列に変換（列順）
  const flattenToColumns = (node) => {
    if (!node.children || node.children.length === 0) {
      return [node];
    }
    return flatMap(node.children, flattenToColumns);
  };

  // 行ごとのヘッダーを生成（多段ヘッダー用）
  const getMaxDepth = (node) => {
    if (!node.children || node.children.length === 0) {
      return node.depth;
    }
    return max(node.children.map(getMaxDepth));
  };

  const maxDepth = getMaxDepth(tree);

  const collectHeaderRows = (node, rows = []) => {
    if (!rows[node.depth]) rows[node.depth] = [];

    // rowspanを計算: 子がなければmaxDepthまで伸ばす
    const rowspan = (!node.children || node.children.length === 0)
      ? maxDepth - node.depth + 1
      : 1;

    rows[node.depth].push({
      ...node,
      rowspan,
    });

    if (node.children) {
      node.children.forEach(child => collectHeaderRows(child, rows));
    }
    return rows;
  };

  const headerRows = collectHeaderRows(tree);
  const columnHeaders = flattenToColumns(tree);

  return { tree, headerRows, columnHeaders, maxDepth };
};

// 後方互換性のため、空のheadersをexport（実際はbuildHeaderTreeを使う）
const headers = [];
const rowTypes = {
  当期首残高: {
    roma: 'toukishu',
    integrates: true,
    columns: (columnItems) => {
      return columnItems.map(_ => ({
        value: _.openingValue(),
        exchangedAmount: _.openingExchangedValue(),
      }));
    },
  },
  会計方針の変更による累積的影響額: {
    roma: 'kaikeihoushinhenkou',
    integrates: true,
    columns: (columnItems) => {
      return columnItems.map(_ => ({
        value: _.policyChangeValue(),
        exchangedAmount: _.policyChangeExchangedValue(),
      }));
    },
  },
  会計方針の変更を反映した当期首残高: {
    columns: (columnItems) => {
      return columnItems.map(_ => ({
        value: _.openingValue() + _.policyChangeValue(),
        exchangedAmount: _.openingExchangedValue() + _.policyChangeExchangedValue(),
      }));
    },
  },
  当期中の変動額: {
    columns: _ => _.map(_ => ({ value: '' })),
    childRows: (columnItems, ssItem) => {
      // 繰越利益剰余金の当期純利益行を先頭に追加
      const netIncomeRow = {
        level: 2,
        label: '当期純利益',
        integrates: true,
        roma: 'toukijunrieki',
        columns: columnItems.map(_ => ({
          value: _.changeItemValues()[0] || 0,
          exchangedAmount: _.changeItemExchangedValues()[0] || 0,
        })),
      };
      // ssItemの変動額行（インデックスは1から始まる）
      const ssItemRows = get(ssItem, 'changeItems', []).map((changeItem, i) => ({
        level: 2,
        label: changeItem.label,
        integrates: true,
        roma: `hendou${i.toString()}`,
        columns: columnItems.map(_ => ({
          value: _.changeItemValues()[i + 1] || 0,
          exchangedAmount: _.changeItemExchangedValues()[i + 1] || 0,
        })),
      }));
      // 為替換算調整額行（為替換算調整勘定の差額を自動表示）
      const changeItemsCount = 1 + get(ssItem, 'changeItems', []).length;
      const exchangeAdjustmentRow = {
        level: 2,
        label: '為替換算調整額',
        integrates: true,
        roma: 'kawasekansanchousei',
        columns: columnItems.map(_ => ({
          value: _.changeItemValues()[changeItemsCount] || 0,
          exchangedAmount: _.changeItemExchangedValues()[changeItemsCount] || 0,
        })),
      };
      return [netIncomeRow, ...ssItemRows, exchangeAdjustmentRow];
    },
  },
  当期変動額合計: {
    columns: (columnItems) => {
      return columnItems.map(_ => ({
        value: sum(_.changeItemValues()),
        exchangedAmount: sum(_.changeItemExchangedValues()),
      }));
    },
  },
  当期末残高: {
    columns: (columnItems) => {
      return columnItems.map(_ => {
        const exchangedAmount = _.openingExchangedValue() + _.policyChangeExchangedValue() + sum(_.changeItemExchangedValues());
        return {
          invalid: floatFormat(_.computedValue()) !== floatFormat(_.closingValue()) || integerFormat(exchangedAmount) !== integerFormat(_.closingExchangedValue()),
          value: _.computedValue(),
          exchangedAmount,
        };
      });
    },
  },
  '当期末残高 (個別修正後)': {
    columns: (columnItems) => {
      return columnItems.map(_ => ({ value: _.closingValue(), exchangedAmount: _.closingExchangedValue() }));
    },
  },
};
// columnHeadersに対応するcolumnItemsを生成
const generateColumnItems = (amountItems, prevYearAmountItems, ssItem, columnHeaders) => {
  // 科目データをIDでキー付け
  const amountItemsById = keyBy(amountItems.filter(_ => _.itemType === 'consolidationAccountItem'), _ => _.consolidationAccountItem?.id || _.item?.id);
  const prevYearAmountItemsById = keyBy(prevYearAmountItems.filter(_ => _.itemType === 'consolidationAccountItem'), _ => _.consolidationAccountItem?.id || _.item?.id);

  // カテゴリデータをキー付け
  const amountItemsByCategory = keyBy(amountItems.filter(_ => _.itemType === 'category'), 'accountItemCategory.name');
  const prevYearAmountItemsByCategory = keyBy(prevYearAmountItems.filter(_ => _.itemType === 'category'), 'accountItemCategory.name');

  // 前期末の当期純損益金額（繰越利益剰余金の当期首残高に加算するため）
  const prevYearNetIncome = prevYearAmountItemsByCategory['当期純損益金額']?.adjustedAmount || 0;
  const prevYearNetIncomeExchanged = prevYearAmountItemsByCategory['当期純損益金額']?.exchangedAmount || 0;

  // 当期の当期純利益（当期中の変動額に表示するため）
  const currentNetIncome = amountItemsByCategory['当期純利益']?.adjustedAmount || 0;
  const currentNetIncomeExchanged = amountItemsByCategory['当期純利益']?.exchangedAmount || 0;

  // 繰越利益剰余金カテゴリ配下の科目のうち、当期純利益の反映先となる科目のIDを1つ特定する
  // 優先度: 「繰越利益剰余金」完全一致 → 「繰越利益」を含む → 「利益剰余金」を含む → カテゴリ内の最初の科目
  const retainedEarningsItems = columnHeaders.filter(_ => _.type === 'item' && _.item.accountItemCategory?.name === '繰越利益剰余金');
  const retainedEarningsTargetItemId = (() => {
    const itemNameOf = _ => _.item.consolidationAccountItem?.name || _.item.item?.name || '';
    const idOf = _ => _.item.consolidationAccountItem?.id || _.item.item?.id;
    const matchers = [
      _ => itemNameOf(_) === '繰越利益剰余金',
      _ => itemNameOf(_).includes('繰越利益'),
      _ => itemNameOf(_).includes('利益剰余金'),
    ];
    const found = matchers.map(matcher => retainedEarningsItems.find(matcher)).find(_ => _);
    return found ? idOf(found) : idOf(retainedEarningsItems[0] || { item: {} });
  })();

  const columnItemsByKey = {};

  const columnItems = columnHeaders.map((header) => {
    const { type, item, category } = header;

    if (type === 'item') {
      // 科目の場合
      const itemId = item.consolidationAccountItem?.id || item.item?.id;
      const itemName = item.consolidationAccountItem?.name || item.item?.name;
      const currentItem = amountItemsById[itemId];
      const prevYearItem = prevYearAmountItemsById[itemId];

      // 繰越利益剰余金カテゴリ配下のうち、当期純利益の反映先となる科目のみ特別処理
      const isRetainedEarnings = item.accountItemCategory?.name === '繰越利益剰余金' && itemId === retainedEarningsTargetItemId;
      const isForeignExchangeAdjustment = item.accountItemCategory?.name === '為替換算調整勘定';

      // 当期首残高: 繰越利益剰余金の場合は前期末の繰越利益剰余金 + 前期末の当期純損益金額
      const openingValue = _ => (prevYearItem?.adjustedAmount || 0) + (isRetainedEarnings ? prevYearNetIncome : 0);
      const openingExchangedValue = _ => (prevYearItem?.exchangedAmount || 0) + (isRetainedEarnings ? prevYearNetIncomeExchanged : 0);
      const policyChangeValue = _ => get(ssItem, ['policyChange', itemId].join('--'), 0);
      const policyChangeExchangedValue = _ => get(ssItem, ['policyChange', itemId, 'jpy'].join('--'), 0);
      // 変動額: 先頭行は「当期純利益」（繰越利益剰余金のみ値あり、他は0）
      const baseChangeItemValues = _ => [
        isRetainedEarnings ? currentNetIncome : 0,
        ...get(ssItem, 'changeItems', []).map((changeItem) => changeItem[itemId] || 0),
      ];
      const baseChangeItemExchangedValues = _ => [
        isRetainedEarnings ? currentNetIncomeExchanged : 0,
        ...get(ssItem, 'changeItems', []).map((changeItem) => {
          const amount = changeItem[itemId] || 0;
          const exchangeRate = changeItem.exchangeRate;
          return exchangeRate ? amount * exchangeRate : amount;
        }),
      ];
      // 繰越利益剰余金の場合は当期純利益も加算
      const closingValue = _ => (currentItem?.adjustedAmount || 0) + (isRetainedEarnings ? currentNetIncome : 0);
      const closingExchangedValue = _ => (currentItem?.exchangedAmount || 0) + (isRetainedEarnings ? currentNetIncomeExchanged : 0);
      // 為替換算調整勘定の場合は差額を自動計算して変動額末尾に追加
      const changeItemValues = _ => {
        const base = baseChangeItemValues();
        if (!isForeignExchangeAdjustment) return base;
        const diff = closingValue() - openingValue() - policyChangeValue() - sum(base);
        return [...base, diff];
      };
      const changeItemExchangedValues = _ => {
        const base = baseChangeItemExchangedValues();
        if (!isForeignExchangeAdjustment) return base;
        const diff = closingExchangedValue() - openingExchangedValue() - policyChangeExchangedValue() - sum(base);
        return [...base, diff];
      };
      const computedValue = _ => openingValue() + policyChangeValue() + sum(changeItemValues());

      const columnItem = {
        header,
        type: 'item',
        itemId,
        itemName,
        isRetainedEarningsTarget: isRetainedEarnings,
        openingValue,
        openingExchangedValue,
        policyChangeValue,
        policyChangeExchangedValue,
        changeItemValues,
        changeItemExchangedValues,
        computedValue,
        closingValue,
        closingExchangedValue,
      };
      columnItemsByKey[`item-${itemId}`] = columnItem;
      return columnItem;
    } else if (type === 'categoryTotal') {
      // カテゴリ計の場合: 子のカテゴリ計と科目を合計
      const categoryName = category.name;

      // このカテゴリに属する科目の合計を計算
      const getChildColumnItems = () => {
        // 末端カテゴリの場合: 科目を合計
        if (category.children().length === 0) {
          return columnItems.filter(_ =>
            _.type === 'item' && _.header.category?.name === categoryName
          );
        }
        // 中間カテゴリの場合: 子カテゴリの計を合計
        return category.children().map(_ => columnItemsByKey[`categoryTotal-${_.name}`]).filter(_ => _);
      };

      // 変動額の行数（当期純利益行 + ssItemの変動額行 + 為替換算調整額行）
      const changeItemsCount = get(ssItem, 'changeItems', []).length + 1 + 1;

      const openingValue = _ => sumBy(getChildColumnItems(), _ => _.openingValue());
      const openingExchangedValue = _ => sumBy(getChildColumnItems(), _ => _.openingExchangedValue());
      const policyChangeValue = _ => sumBy(getChildColumnItems(), _ => _.policyChangeValue());
      const policyChangeExchangedValue = _ => sumBy(getChildColumnItems(), _ => _.policyChangeExchangedValue());
      // 変動額: 子の各行の合計
      const changeItemValues = _ => {
        const childItems = getChildColumnItems();
        return Array(changeItemsCount).fill(0).map((__, i) => sumBy(childItems, c => c.changeItemValues()[i] || 0));
      };
      const changeItemExchangedValues = _ => {
        const childItems = getChildColumnItems();
        return Array(changeItemsCount).fill(0).map((__, i) => sumBy(childItems, c => c.changeItemExchangedValues()[i] || 0));
      };
      const computedValue = _ => sumBy(getChildColumnItems(), _ => _.computedValue());
      // 子の closingValue の合計（繰越利益剰余金には当期純利益が含まれている）
      const closingValue = _ => sumBy(getChildColumnItems(), _ => _.closingValue());
      const closingExchangedValue = _ => sumBy(getChildColumnItems(), _ => _.closingExchangedValue());

      const columnItem = {
        header,
        type: 'categoryTotal',
        categoryName,
        openingValue,
        openingExchangedValue,
        policyChangeValue,
        policyChangeExchangedValue,
        changeItemValues,
        changeItemExchangedValues,
        computedValue,
        closingValue,
        closingExchangedValue,
      };
      columnItemsByKey[`categoryTotal-${categoryName}`] = columnItem;
      return columnItem;
    }

    // fallback
    return {
      header,
      openingValue: _ => 0,
      openingExchangedValue: _ => 0,
      policyChangeValue: _ => 0,
      policyChangeExchangedValue: _ => 0,
      changeItemValues: _ => [],
      changeItemExchangedValues: _ => [],
      computedValue: _ => 0,
      closingValue: _ => 0,
      closingExchangedValue: _ => 0,
    };
  });

  return columnItems;
};

const generateRows = (amountItems, prevYearAmountItems, ssItem) => {
  const { columnHeaders } = buildHeaderTree(amountItems);
  const columnItems = generateColumnItems(amountItems, prevYearAmountItems, ssItem, columnHeaders);
  const rows = entries(rowTypes).map(([rowType, { columns, integrates, roma, childRows = _ => [] }]) => {
    return [{
      level: 1,
      label: rowType,
      integrates,
      roma,
      columns: columns ? columns(columnItems) : columnItems.map(() => {
        return {};
      }),
    }, ...childRows(columnItems, ssItem)];
  }).flat();
  return rows;
};

const rowsForExport = (...args) => () => {
  const [amountItems] = args;
  const { columnHeaders } = buildHeaderTree(amountItems);
  const columnItems = generateColumnItems(...args, columnHeaders);
  return generateRows(...args).map((row) => {
    const { level, label, columns, } = row;
    return {
      項目: level === 1 ? label : '',
      内訳: level === 2 ? label : '',
      ...(
        columnItems.reduce((x, columnItem, i) => {
          return {
            ...x,
            [columnItem.header.label]: columns[i].value,
          };
        }, {})
      ),
    };
  });
};

const computeAlerts = (...args) => {
  const rows = generateRows(...args);
  const closingRow = rows.find(_ => _.label === '当期末残高');
  return [
    closingRow?.columns.some(_ => _.invalid) && '当期末残高に差額が発生しています',
  ].filter(_ => _);
};

// 科目ごとの当期末残高（換算後）を計算してexchangedItem用のitemsを返す
// 繰越利益剰余金は当期末残高から当期純利益を引いた金額で転記
const computeExchangedItems = (amountItems, prevYearAmountItems, ssItem) => {
  const { columnHeaders } = buildHeaderTree(amountItems);
  const columnItems = generateColumnItems(amountItems, prevYearAmountItems, ssItem, columnHeaders);
  return columnItems
    .filter(_ => _.type === 'item' && _.header.category?.name !== '為替換算調整勘定')
    .map(_ => {
      // 当期末残高（換算後）= 当期首残高 + 会計方針変更 + 変動額
      const closingExchangedAmount = _.openingExchangedValue() + _.policyChangeExchangedValue() + sum(_.changeItemExchangedValues());
      // 繰越利益剰余金の反映先科目の場合は当期純利益（changeItemExchangedValues()[0]）を引く
      const amount = _.isRetainedEarningsTarget ? closingExchangedAmount - (_.changeItemExchangedValues()[0] || 0) : closingExchangedAmount;
      return {
        accountItemId: _.itemId,
        amount,
      };
    })
    .filter(_ => _.amount !== 0);
};


exports.generateRows = generateRows;
exports.rowsForExport = rowsForExport;
exports.headers = headers;
exports.buildHeaderTree = buildHeaderTree;
exports.computeAlerts = computeAlerts;
exports.computeExchangedItems = computeExchangedItems;
